import { HistoryRouterBase } from './history-router-base';
import { URLParameterKey } from '../url-manager/url-manager-category';
import { SearchRouteState } from '../../types/catalog';

// Choices are '<percentage>_100' (e.g. '5_100', see CatalogFacetConfigService). A marketing link
// that appends a query param after the hash instead of before it (e.g.
// '#max-discount-rate=5_100?utm_source=google') gets parsed by URLSearchParams as one literal
// value, '5_100?utm_source=google' - the intended tier is still '5_100', just with a stray query
// string glued onto it (URLSearchParams only splits on '&', so it never sees the '?' as a
// separator). Stripping everything from the first '?' onward before validating recovers the
// real, intended value instead of discarding it outright - dropping the whole thing would lose
// the retailer's actual filter, not just the tracking noise.
const MAX_DISCOUNT_RATE_CHOICE_PATTERN = /^\d+_100$/;
const stripTrailingQueryString = (value: string) => value.split('?')[0];

export class HistoryRouterProduct extends HistoryRouterBase {
  read() {
    const hashParams = this.getHashParams(this.currentRoute);
    const categories = this.urlManager().getCategoriesFromRoute(this.getCategories(), this.currentRoute);
    const locations = this.urlManager().getLocationsFromRoute(this.getLocations(), this.currentRoute);

    const hierarchicalMenu = this.removeEmptyValues({
      'catalog_hierarchical_categories.v3.lvl0': categories.map((category) => `${category.id}`),
    });
    const attributeRefinements = this.getAttributeRefinements(hashParams);
    const refinementList = this.removeEmptyValues({
      'brand.location': locations.map((location) => location.id),
      'brand.name': hashParams.get(URLParameterKey.Brands)?.split(',') ?? [],
      margin: hashParams.get(URLParameterKey.Margin)?.split(',') ?? [],
      [`made_in.${this.getLang()}`]: hashParams.get(URLParameterKey.MadeIn)?.split(',') ?? [],
      [`tags.${this.getLang()}`]: hashParams.get(URLParameterKey.Values)?.split(',') ?? [],
      'brand.shipping_lead_time': hashParams.get(URLParameterKey.Shipping)?.split(',') ?? [],
      discount: hashParams.get(URLParameterKey.Discount)?.split(',') ?? [],
      'max_discount_rate.range':
        hashParams
          .get(URLParameterKey.MaxDiscountRate)
          ?.split(',')
          .map(stripTrailingQueryString)
          .filter((value) => MAX_DISCOUNT_RATE_CHOICE_PATTERN.test(value)) ?? [],
      'brand.aks_plus': hashParams.get(URLParameterKey.loyaltyOffer)?.split(',') ?? [],
      aks_systems: hashParams.get(URLParameterKey.aksSystems)?.split(',') ?? [],
      storage_temperature: hashParams.get(URLParameterKey.StorageTemperature)?.split(',') ?? [],
      alcohol_content: hashParams.get(URLParameterKey.AlcoholContent)?.split(',') ?? [],
      'product_type.id': hashParams.get(URLParameterKey.ProductType)?.split(',') ?? [],
      ...attributeRefinements,
    });
    const retailPriceRangeMin = hashParams.get(URLParameterKey.retailPriceRangeMin) ?? '';
    const retailPriceRangeMax = hashParams.get(URLParameterKey.retailPriceRangeMax) ?? '';
    const attributeRanges = this.getAttributeRanges(hashParams);
    const attributesToggle = this.getAttributeToggles(hashParams);

    const range = this.removeEmptyValues({
      [`retail_prices.${this.getCurrency()}`]:
        retailPriceRangeMin || retailPriceRangeMax ? `${retailPriceRangeMin}:${retailPriceRangeMax}` : '',
      ...attributeRanges,
    });
    const toggle = this.removeEmptyValues(attributesToggle);
    const textQuery = this.currentRoute.query.q?.toString() ?? '';
    const page = parseInt(this.currentRoute.query.p?.toString() ?? '');

    const routeState: SearchRouteState = {
      ...this.removeEmptyValues({
        hierarchicalMenu,
        refinementList,
        range,
        toggle,
      }),
    };

    if (textQuery) {
      routeState.q = textQuery;
    }

    if (page) {
      routeState.page = page;
    }

    return routeState;
  }
}
